import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import {
  AuthHttpService,
  EventSubHttpService,
  InsightService,
} from '@sc/data-access-http-services';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, timeout } from 'rxjs/operators';
import { AUTH_OKTA_MODULE_CONFIG, AuthOktaService } from '../../services';
import { AuthOktaConfig } from '../../types';

export const DEFAULT_TIMEOUT = new InjectionToken<number>('defaultTimeout');

@Injectable({
  providedIn: 'root',
})
export class TokenInterceptor implements HttpInterceptor {
  constructor(
    private readonly authOktaService: AuthOktaService,
    private readonly insightService: InsightService,
    private readonly authHttpService: AuthHttpService,
    private readonly eventSubHttpService: EventSubHttpService,
    @Inject(AUTH_OKTA_MODULE_CONFIG) protected authOktaConfig: AuthOktaConfig,
    @Inject(DEFAULT_TIMEOUT) protected defaultTimeout
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return this.handleAccess(request, next).pipe(
      catchError((err) => {
        if (err?.status === 401) {
          this.insightService.trackEvent({
            name:
              'FrontApp logout forced caused by 401 ' +
              this.authOktaService.getAccountLogin() +
              ' on request ' +
              request.url,
            properties: {
              request,
              err,
            },
          });
          if (request.url.indexOf('gqlsub') == -1) {
            this.authOktaService.notifyAndLogout();
          } else {
            this.authOktaService.logOutAndRedirect();
          }
        } else if (err?.status >= 500 && request.url.indexOf('gqlsub') > -1) {
          // Force logout, if no connection with subscription service (504 Gateway Timeout for example)
          this.authOktaService.logOutAndRedirect();
        }
        return throwError(err);
      })
    );
  }

  private handleAccess(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const accessToken = this.authOktaService.getAccessToken();
    const requestId = this.insightService.generateRequestId();
    //this.fireInsightTrackEvent(request, requestId);

    const clientKey = this.eventSubHttpService.getClientKey();

    const timeoutValue = request.headers.get('timeout') || this.defaultTimeout;
    const timeoutValueNumeric = Number(timeoutValue);

    const currentTime = new Date();
    const expireTokenPeriod: Date = new Date(
      this.authOktaService.getExpireAccessTokenDate() * 1000
    );
    if (
      expireTokenPeriod &&
      (expireTokenPeriod.getTime() - currentTime.getTime()) / (1000 * 60) < 5 &&
      (currentTime.getTime() - expireTokenPeriod.getTime()) / (1000 * 3600) <
        24 &&
      !request.url.includes('token') &&
      this.authOktaService.getRefreshToken()
    ) {
      console.log('REFRESH TOKEN');
      return this.requestWithRefreshToken(
        request,
        next,
        requestId,
        clientKey,
        timeoutValueNumeric
      )?.pipe(
        catchError((error) => {
          // Problem at refreshing token
          console.log(error);
          this.authOktaService.clearRefreshToken();
          return of(null);
        })
      );
    }

    request = request.clone({
      setHeaders: {
        Authorization: 'Bearer ' + accessToken,
        requestId,
        clientKey,
      },
    });
    return next.handle(request).pipe(timeout(timeoutValueNumeric));
  }

  private requestWithRefreshToken(
    request: HttpRequest<any>,
    next: HttpHandler,
    requestId: string,
    clientKey: string,
    timeoutValueNumeric: number
  ): Observable<HttpEvent<any>> {
    return this.authHttpService
      .refreshAccessToken(this.authOktaService.getRefreshToken())
      .pipe(
        map((response) => response),
        switchMap((response: any) => {
          this.authOktaService.setAccessToken(response?.accessToken);
          this.authOktaService.setIdToken(response?.idToken);
          this.authOktaService.setRefreshToken(response?.refreshToken);

          request = request.clone({
            setHeaders: {
              Authorization: 'Bearer ' + response?.accessToken?.value,
              requestId,
              clientKey,
            },
          });

          return next.handle(request).pipe(timeout(timeoutValueNumeric));
        })
      );
  }

  private fireInsightTrackEvent(request: HttpRequest<any>, requestId: string) {
    const requestName = request.headers.get('requestName');
    if (!this.requestInsightToIgnore(requestName)) {
      const host = request.headers.get('Host');
      this.insightService.trackEvent({
        name: 'FrontApp ' + host + ' ' + requestName + ' start',
        properties: {
          body: request.body,
          requestId,
        },
      });
    }
  }

  requestInsightToIgnore(requestName: string) {
    return (
      !requestName ||
      requestName === 'auth-http refreshAccessToken' ||
      requestName === 'config-http fetchConfig'
    );
  }
}
